/**
 * message.ts
 * Secured Communications
 *
 * Created by: Dylan Knight
 * Copyright (c) 2021 Secured Communications. All rights reserved.
 *
**/

import { DateTime } from "luxon";
import moment from "moment-timezone";

import { Membership, MemberType } from "./auth";
import { GroupPKI, ProtocolScheme } from "./group";
import { Reaction, ReactionContentType } from "./reaction";

export enum MessageFrameType {
  EXPIRED_TOKEN = "expired",
  ACK_OPEN = "ackopen",
  START_TYPING_CMD = "start-typing",
  STOP_TYPING_CMD = "stop-typing",
  CONTROL = "ctrl",
  HUDDLE_START = "hudStart",
  HUDDLE_END = "hudEnd",
  HUDDLE_REQ_RESULT = "hudReqRes",
  NEW_WAITING = "mtgNewU",
  REM_WAITING = "mtgRemWait",
  MTG_WAITING_DECISION = "mtgWait",

  MESSAGE = "msg",
  APP_BACKGROUND = "bkgrd",
  WELCOME = "welcome",
  EXT_WELCOME = "extwel",
  REWELCOME = "rewel",
  ADD_MEMBERS = "add",
  REMOVE_MEMBER = "rem",
  REMOVE_MEMBER_REQ = "remreq",
  EXT_ADD = "extadd",
  RECOVER = "recover",
  REMOVE = "remgrp",
  REMOVE_MSG = "remmsg",
  REMOVE_REPLY = "remrpy",
  NEW_DATA_KEY = "datakey",
  SCHEME_CHANGE = "schg"
}

export interface MeetingMessageFrame {
  _id: string,
  cmd: MessageFrameType,
  accountID: string,
  meetingID: string,
  fromUserID: string,
  fromDeviceID?: string,
  fromPhoneNumber?: string,
  toUserID?: string,
  firstname?: string,
  lastname?: string,
  allowed?: boolean,
  membertype?: MemberType
  sent?: moment.Moment | Date,
}

export enum NotifyLevel {
  silent = 1,
  normal = 2,
  elevated = 3,
  urgent = 4
}

export interface NotifyUser {
  team?: string,
  teamID?:string, 
  userID: string,
  level: NotifyLevel
}

export interface MessageFrame {
  _id: string,
  cmd: MessageFrameType,
  accountID: string,
  groupID: string,
  fromUserID: string,
  fromDeviceID: string,
  data: Uint8Array,
  created: moment.Moment | Date,
  sent?: moment.Moment | Date,
  epoch: number,
  msgCounter: number,
  senderData: Uint8Array,
  signature?: Uint8Array,
  notifyUsers?: Array<NotifyUser | string>,
  pki: GroupPKI,
  scheme?: ProtocolScheme,
  keyidx?: number,
  version: number,
  hasAcked?: boolean
}

export enum MessageType {
  CONTENT = "content",
  READ = "readg",
  CTRL = "ctrl",
  REACTION = "react",
  REMOVE_MSG = "remmsg",
  REMOVE_REPLY = "remrpy",
  NEW_DATA_KEY = "datakey",
  USER_JOINED = "usrjoin",
  DEV_JOINED = "devjoin",
  USER_ADDED = "usradded",
  DEV_ADDED = "devadded",
  DEV_REM = "devrem",
  HISTORY = "hist"
}

export enum ContentType {
  html = "text/html",
  plain = "text/plain",
  markdown = "text/markdown"
}

export interface MessageData {
  _id?: string,
  msgtype?: MessageType
}

export interface DisplayMessage extends MessageData {
  _id: string,
  frameID?: string,
  msgtype: MessageType,
  //accountID: string,
  userID: string,
  groupID: string,
  threadID: string,
  created: moment.Moment | DateTime | Date,

  // client-side only, cache for display
  isUnread?: boolean,
  isMention?: boolean,
  participatingInThread?: boolean,
  lastReply?: moment.Moment | DateTime | Date,
  firstname?: string,
  lastname?: string,
  acctMember?: Membership
}

export interface RemoveMsgMessage extends MessageData {
  _id: string,
  msgID: string,
  threadID?: string,
  msgtype: MessageType.REMOVE_MSG | MessageType.REMOVE_REPLY
}

export interface NewDataKeyMessage extends MessageData {
  _id: string,
  msgtype: MessageType.NEW_DATA_KEY
  keyID: string,
  secret: Uint8Array
}

export interface DevicesAddedMessage extends DisplayMessage {
  _id: string,
  accountID: string,
  msgtype: MessageType.USER_ADDED | MessageType.DEV_ADDED
  devices: Array<{_id: string, userID: string}>,
  created: moment.Moment | DateTime | Date,

  userSet?: Set<string>,
  namesList?: string
}

export interface DeviceJoinedMessage extends DisplayMessage {
  _id: string,
  accountID: string,
  msgtype: MessageType.DEV_JOINED | MessageType.USER_JOINED,
  deviceID: string,
  deviceName: string,

  firstname?: string,
  lastname?: string,
  acctMember?: Membership
}

export interface HistoryPreservationMessage extends DisplayMessage {
  _id: string,
  accountID: string,
  msgtype: MessageType.HISTORY,
  created: DateTime | Date
  isPreserved: boolean
}

/* eslint-disable @typescript-eslint/no-unsafe-declaration-merging */
export interface ContentMessage extends DisplayMessage {
  _id: string,
  sent?: moment.Moment | Date,
  content: string,
  contenttype: ContentType,
  //inReplyTo?: string,
  replyCount: number,
  replyUsers?: Array<string>,
  lastReply?: moment.Moment | DateTime | Date,

  // client-only
  firstname?: string, // in case the acct membership is revoked
  lastname?: string,
  sanitizedContent?: string,
  isUnread?: boolean,
  isMention?: boolean,
  acctMember?: Membership
  reactions?: {[id: string]: Reaction},
}

export class ContentMessage implements ContentMessage {

  constructor (obj: ContentMessage) {
    Object.assign(this, obj);
    if (this.created) {
      this.created = moment(this.created, moment.ISO_8601);
    }
  }
}
/* eslint-enable @typescript-eslint/no-unsafe-declaration-merging */

export interface ReactionMessage extends MessageData {
  _id: string,
  msgtype: MessageType.REACTION,
  isRemoving: boolean,
  userID: string,
  groupID: string,
  threadID?: string,
  msgID: string,
  reactionID?: string,

  created: DateTime | Date,
  contenttype: ReactionContentType,
  contentID: string,
  content: string,

  firstname: string,
  lastname: string
}

export interface SenderData {
  sender: number,
  generation: number,
  reuseGuard: Uint8Array,
  senderID?: string,
  deviceID?: string
}

export interface RemoveMembersRequestPayload {
  userID?: string,
  deviceID?: string,
  userIDs?: Array<string>,
  deviceIDs?: Array<string>
}

export interface ArchiveEntry {
  _id: string,
  msgtype: MessageType,
  accountID: string,
  userID: string,
  deviceID?: string,
  groupID: string,
  threadID?: string,
  msgID?: string,
  created: DateTime,
  sent: DateTime,
  content?: string,
  contenttype?: ContentType,
  firstname?: string,
  lastname?: string,
  groupname?: string,
  deviceName?: string,

  // client-only
  sanitizedContent?: string
}

export function updateCachedMemberNameAsNeeded(
  msg: DisplayMessage, mem: Membership | null): boolean {
let sameNames = true;
if (mem) {
  msg.acctMember = mem;
  if (mem.firstname !== msg.firstname) {
    msg.firstname = mem.firstname;
    sameNames = false;
  }
  if (mem.lastname !== msg.lastname) {
    msg.lastname = mem.lastname;
    sameNames = false;
  }
}
return sameNames;
}
